import { get, set, del, createStore } from 'idb-keyval'
import randomStateIdValue from '../core/state/randomStateIdValue'

// Custom store for avatar blobs
// Key format: avatar_${id} where id is a positive integer
const avatarsStore = createStore('st1_avatars', 'avatars')

/**
 * Save an avatar blob to IndexedDB
 * @param id - The avatar ID (positive integer, same range as state IDs)
 * @param blob - The avatar blob to store
 */
export async function saveAvatar(id: number, blob: Blob): Promise<void> {
  const key = `avatar_${id}`
  await set(key, blob, avatarsStore)
}

/**
 * Load an avatar blob from IndexedDB
 * @param id - The avatar ID to retrieve
 * @returns The blob or null if not found
 */
export async function loadAvatar(id: number): Promise<Blob | null> {
  const key = `avatar_${id}`
  const result = await get(key, avatarsStore)
  return result || null
}

/**
 * Delete an avatar blob from IndexedDB
 * @param id - The avatar ID to delete
 */
export async function deleteAvatar(id: number): Promise<void> {
  const key = `avatar_${id}`
  await del(key, avatarsStore)
}

/**
 * Generate a unique avatar ID for IndexedDB storage
 * Uses the same ID generation as state IDs to maintain consistency
 * @returns A unique positive integer ID
 */
export async function generateAvatarId(): Promise<number> {
  const maxAttempts = 100
  for (let i = 0; i < maxAttempts; i++) {
    const id = randomStateIdValue()
    const existing = await loadAvatar(id)
    if (!existing) {
      return id
    }
  }
  throw new Error('Failed to generate unique avatar ID after multiple attempts')
}
